import type { ApolloClient } from '@apollo/client';
import type { ResultOf } from '@newfront-insurance/graphql-frontend';

import { GET_SETTINGS_ORGANIZATION_MEMBERSHIP } from '../../../../queries/skill-administration';
import { getEmployeeDisplayName, type SettingsEmployee } from '../settings-people/employee-utils';

/** Returns only definite nonmembers. Inherited membership still requires the backend permission check. */
export async function findOrganizationNonMembers(
  client: Pick<ApolloClient<object>, 'query'>,
  organizationUuid: string,
  employees: readonly SettingsEmployee[],
): Promise<SettingsEmployee[]> {
  const { data } = await client.query({
    query: GET_SETTINGS_ORGANIZATION_MEMBERSHIP,
    variables: { groupId: organizationUuid },
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
    context: { queryDeduplication: false, notifications: { preventErrorNotification: true } },
  });
  const members = getOrganizationMemberUuids(data?.getSkillGroup, organizationUuid);
  return members ? employees.filter((employee) => !members.has(employee.userUuid)) : [];
}

/** Undefined means inherited roles make the direct membership lists incomplete. */
export function getOrganizationMemberUuids(
  group: ResultOf<typeof GET_SETTINGS_ORGANIZATION_MEMBERSHIP>['getSkillGroup'] | null | undefined,
  organizationUuid: string,
): ReadonlySet<string> | undefined {
  if (
    group?.uuid !== organizationUuid ||
    group.parentGroupUuid !== null ||
    !Array.isArray(group.members) ||
    !Array.isArray(group.admins) ||
    !Array.isArray(group.managers) ||
    !Array.isArray(group.inheritedRoles)
  ) {
    throw new Error('Organization membership is unavailable');
  }
  // Direct UUIDs are not a complete membership list when business roles contribute membership.
  // Do not recreate AuthZed role evaluation in React or reject someone solely because they are absent here.
  if (group.inheritedRoles.length) return undefined;
  return new Set([...group.members, ...group.admins, ...group.managers]);
}

export function organizationNonMembersMessage(employees: readonly SettingsEmployee[]) {
  const names = [
    ...new Map(employees.map((employee) => [employee.userUuid, getEmployeeDisplayName(employee)])).values(),
  ];
  return `The following people are not part of the organization: ${names.join(', ')}.`;
}
